<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import isJSONString from '../../hooks/isJsonString';
import stringToJSON from '../../hooks/stringToJSON';

const props = defineProps({
  dropdownTitle: {
    type: String,
    default: '',
  },
  dropdownLabel: {
    type: String,
    default: 'Select',
  },
  name: {
    type: String,
    default: '',
  },
  dropdownIcon: {
    type: String,
    default: 'icon-triangle',
  },
  iconRed: {
    type: String,
    default: 'false',
  },
  error: {
    type: String,
    default: '',
  },
  valueIndexOnMount: {
    type: [Number, String],
    default: -1,
  },
  optionsArray: {
    type: Array,
    default: () => [],
  },
  classes: {
    type: String,
    default: '',
  },
  callBack: {
    type: Function,
    default: () => {},
  },
  isPopOut: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Object,
    default: () => undefined,
  },
  showValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:value', 'update:modelValue']);

const randomId = Math.random().toString(36).substring(2, 12);
const isOpen = ref(false);
const currentLabel = ref(
  props.modelValue?.label ? props.modelValue.label : props.dropdownLabel,
);
const currentValue = ref(props.modelValue?.value ? props.modelValue.value : '');
const currentImg = ref(props.modelValue?.imgsrc ? props.modelValue.imgsrc : '');
const optionsRef = ref([]);
const eventsSet = ref(false);
const listRef = ref();
const inputRef = ref();
const selectRef = ref();
const buttonRef = ref();
const dropdownRef = ref();
const mountIndex = ref(-1);
const keyboardIndex = ref(0);

const toggleDropdownHandler = () => {
  isOpen.value = !isOpen.value;
  buttonRef.value?.focus();

  keyboardIndex.value = 0;
  optionsRef.value.forEach((option) => {
    option.isActive = false;
  });
  setTimeout(() => {
    const rect = dropdownRef.value.getBoundingClientRect();
    const elemCurrentHeight = rect.bottom - rect.top;
    const maxAllowedHeight = window.innerHeight - rect.top - 30;
    const list = dropdownRef.value.querySelector('ul');
    if (elemCurrentHeight > maxAllowedHeight) {
      list.style.height = maxAllowedHeight + 'px';
    }
  }, 100);
};

const resetHandler = () => {
  currentLabel.value = props.dropdownLabel;
  currentValue.value = '';
  currentImg.value = '';
};

defineExpose({
  resetHandler,
});

const selectionHandler = (label, value, img) => {
  // inputRef.value.value = value
  currentLabel.value = label;
  currentValue.value = value;
  currentImg.value = img;
  emit('update:value', {
    value: currentValue.value,
    label: currentLabel.value,
    name: props.name,
    imgsrc: currentImg.value,
  });
  // for two way data binding
  emit('update:modelValue', {
    value: currentValue.value,
    label: currentLabel.value,
    imgsrc: currentImg.value,
  });
  props.callBack({
    value: currentValue.value,
    label: currentLabel.value,
    name: props.name,
    imgsrc: currentImg.value,
  });
  if (isOpen.value) {
    toggleDropdownHandler();
  }
};

const handleEnterKey = () => {
  if (
    keyboardIndex.value >= 0 &&
    keyboardIndex.value < optionsRef.value.length
  ) {
    const activeOption = optionsRef.value[keyboardIndex.value];
    selectionHandler(
      activeOption.label,
      activeOption.value,
      activeOption.imgsrc,
    );
    toggleDropdownHandler();
  }
};

const handleArrowKey = (event) => {
  event.preventDefault();

  const newIndex =
    event.key === 'ArrowUp'
      ? (keyboardIndex.value - 1 + optionsRef.value.length) %
        optionsRef.value.length
      : (keyboardIndex.value + 1) % optionsRef.value.length;

  optionsRef.value.forEach((option) => {
    option.isActive = false;
  });

  keyboardIndex.value = newIndex;
  optionsRef.value[newIndex].isActive = true;

  nextTick(() => {
    listRef.value.children[newIndex]?.scrollIntoView({
      block: 'nearest',
    });
  });
};

const handleCharacterKey = (char) => {
  let matchingOptions = [];
  for (let i = 0; i < optionsRef.value.length; i++) {
    if (optionsRef.value[i].label[0].toLowerCase() === char) {
      matchingOptions.push(i);
    }
  }

  handleMatchingOptions(matchingOptions);
};

const handleMatchingOptions = (matchingOptions) => {
  if (matchingOptions.length > 0) {
    const currentIndex = matchingOptions.indexOf(keyboardIndex.value);
    const nextIndex =
      currentIndex !== -1 ? (currentIndex + 1) % matchingOptions.length : 0;
    const newIndex = matchingOptions[nextIndex];

    optionsRef.value.forEach((option) => {
      option.isActive = false;
    });

    keyboardIndex.value = newIndex;
    optionsRef.value[newIndex].isActive = true;

    nextTick(() => {
      listRef.value.scrollTop = listRef.value.children[newIndex].offsetTop;
    });
  } else {
    keyboardIndex.value = 0;

    nextTick(() => {
      listRef.value.children[keyboardIndex.value]?.scrollIntoView({
        block: 'nearest',
      });
    });

    optionsRef.value.forEach((option) => {
      option.isActive = false;
    });
    optionsRef.value[keyboardIndex.value].isActive = true;
  }
};

const handleKeyDown = (event) => {
  if (event.key === 'Enter') {
    handleEnterKey();
    return;
  }

  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    handleArrowKey(event);
  }

  if (event.key.length === 1 && event.key.match(/[a-z0-9]/i)) {
    const char = event.key.toLowerCase();
    handleCharacterKey(char);
  }
};

const setValueOnMountHandler = () => {
  let entry = {};
  if (isJSONString(props.optionsArray) && mountIndex.value > -1) {
    entry = JSON.parse(props.optionsArray)[props.valueIndexOnMount];
    currentLabel.value = entry.label;
    currentValue.value = entry.value;
    currentImg.value = entry.imgsrc;
    emit('update:value', {
      value: currentValue.value,
      label: currentLabel.value,
      imgsrc: currentImg.value,
    });
    return;
  }

  if (props.optionsArray?.length > 0 && mountIndex.value > -1) {
    entry = props.optionsArray[mountIndex.value];
    currentLabel.value = entry.label;
    currentValue.value = entry.value;
    currentImg.value = entry.imgsrc;

    // we need to emit here so that it can still be used by outside components
    emit('update:value', {
      value: currentValue.value,
      label: currentLabel.value,
      imgsrc: currentImg.value,
    });
  }
};

const computedClasses = computed(() => {
  return {
    hasError: props.error.length > 0,
    pop: props.isPopOut,
    isOpen: isOpen.value === true,
    [`${props.classes}`]: true,
  };
});

const computedIconClasses = computed(() => {
  return {
    hasError: props.error.length > 0,
    'icon-color--red': props.iconRed === 'true',
    [`${props.dropdownIcon}`]: props.dropdownIcon,
  };
});

const collapseHandler = (e) => {
  const target = e?.target;
  if (selectRef.value?.contains(target)) {
    return;
  }
  toggleDropdownHandler();
};

const tabHandler = (e) => {
  if (e.which === 9 && !e.shiftKey) {
    e.preventDefault();
    const srcElement = e.srcElement;
    const srcParent = srcElement.parentNode;
    const listItems = listRef.value.querySelectorAll('li');
    const listLength = listItems.length;
    const srcIndex = Array.from(listItems).indexOf(srcParent);
    if (srcIndex + 1 < listLength) {
      listItems[srcIndex + 1]
        .querySelector('button')
        .focus({ focusVisible: true });
    } else {
      buttonRef.value.focus({ focusVisible: true });
    }
  }
};

const windowEventsHandler = (bool) => {
  if (bool === eventsSet.value) {
    return;
  }
  eventsSet.value = bool;

  if (bool) {
    window.addEventListener('click', collapseHandler);
    window.addEventListener('scroll', collapseHandler);
    const listItems = listRef.value.querySelectorAll('li');
    for (const li of listItems) {
      li.querySelector('button')?.addEventListener('keydown', tabHandler);
    }
  } else {
    window.removeEventListener('click', collapseHandler);
    window.removeEventListener('scroll', collapseHandler);
    const listItems = listRef.value.querySelectorAll('li');
    for (const li of listItems) {
      li.querySelector('button')?.removeEventListener('keydown', tabHandler);
    }
  }
};

watch(isOpen, () => {
  windowEventsHandler(isOpen.value);
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal && newVal.value !== currentValue.value) {
      selectionHandler(newVal.label, newVal.value, newVal.imgsrc);
    }
  },
  { deep: true, immediate: true },
);

const setOptions = () => {
  if (typeof props.optionsArray === 'object') {
    if (props.optionsArray.length > 0) {
      optionsRef.value = props.optionsArray;
    }
  } else if (typeof props.optionsArray === 'string') {
    // Check if it is a JSON string
    if (isJSONString(props.optionsArray)) {
      optionsRef.value = JSON.parse(props.optionsArray);
    } else {
      // Try to change a JSON like string into a JSON string
      const newJSONObj = stringToJSON(props.optionsArray);
      // Check if it is a JSON string
      if (isJSONString(newJSONObj)) {
        optionsRef.value = JSON.parse(newJSONObj);
      }
    }
  }
  if (typeof props.valueIndexOnMount === 'string') {
    const number = parseInt(props.valueIndexOnMount);
    if (!isNaN(number)) {
      mountIndex.value = number;
    }
  }

  if (typeof props.valueIndexOnMount === 'number') {
    mountIndex.value = props.valueIndexOnMount;
  }
};

onMounted(() => {
  setOptions();

  if (!props.modelValue) {
    setValueOnMountHandler();
  }
});

watch(
  () => props.optionsArray,
  () => {
    setOptions();
    if (!props.modelValue) {
      setValueOnMountHandler();
    }
  },
);
</script>

<template>
  <div ref="selectRef" class="select" :class="computedClasses">
    <button
      ref="buttonRef"
      type="button"
      class="button select__trigger"
      :class="{ hasImg: currentImg }"
      :aria-expanded="isOpen ? true : false"
      :aria-controls="randomId"
      :aria-label="ariaLabel"
      @click="toggleDropdownHandler"
      @keydown="handleKeyDown"
    >
      <div v-if="currentImg" class="select__listimg">
        <img :src="currentImg" alt="country flag" />
      </div>
      <div class="select__labels">
        <p v-if="dropdownTitle" class="title fs-12">{{ dropdownTitle }}</p>
        <p class="label m-b--0 text-KQ-Grey1 truncate">
          {{ showValue ? currentValue : currentLabel }}
        </p>
      </div>
      <i class="icon" :class="computedIconClasses" aria-hidden="true"></i>
    </button>
    <p v-if="error.length" class="select__error">{{ error }}</p>
    <div ref="dropdownRef" class="select__dropdown">
      <ul ref="listRef" class="ul--cta ul--cta-lg">
        <li
          v-for="option in optionsRef"
          :key="option.value"
          @click="
            () => {
              selectionHandler(option.label, option.value, option.imgsrc);
            }
          "
        >
          <button
            :class="[
              'button',
              'button--iconright',
              'select__dropdown-option',
              { active: option.isActive },
              { hasImg: option.imgsrc },
            ]"
            type="button"
          >
            <div v-if="option.imgsrc" class="select__listimg">
              <img :src="option.imgsrc" alt="country flag" />
            </div>
            {{ option.label }}
            <i
              v-if="currentValue === option.value"
              class="icon icon-check icon-color--red icon--xs"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
